/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

// Custom hook to fetch product availabilities
import { useState, useEffect, useCallback } from 'react';
import { Product, getStockAvailabilities } from '../../storage';
import { CartItem } from '../../../cart/storage/cart.types';
import { AppDispatch, useAppDispatch } from '../../../../store/rootStore';
import { StocksSlice } from '../../storage/stocks.slice';
import { debounce } from 'lodash';

export const mergeStockWithProducts = async (
  products: Product[] | CartItem[],
  dispatch: AppDispatch,
) => {
  const productIds = products.map(({ productId }) => productId);
  const items = await getStockAvailabilities({ productIds });
  if (items && items.length > 0) dispatch(StocksSlice.actions.addAvailabilities(items));

  return products.map((product) => {
    const stockAvailabilities = items?.filter((item) => item.productId === product.productId);
    return { ...product, stockAvailabilities };
  });
};

interface Props {
  products: Product[] | CartItem[];
  setState?: React.Dispatch<React.SetStateAction<boolean>>;
}
const useStockAvailabilities = ({ products, setState }: Props) => {
  const dispatch = useAppDispatch();

  const [productsWithStock, setProductsWithStock] = useState<Product[] | CartItem[] | undefined>(
    undefined,
  );

  const debouncedMergeProducts = useCallback(
    debounce(async (products: Product[] | CartItem[]) => {
      if (setState) setState(true);
      const mergedData = await mergeStockWithProducts(products, dispatch);
      if (mergedData) {
        setProductsWithStock(mergedData);
        if (setState) setState(false);
      }
    }, 300),
    [dispatch, setState],
  );

  useEffect(() => {
    if (products.length > 0) {
      debouncedMergeProducts(products);
    } else {
      setProductsWithStock([]);
      if (setState) setState(false);
    }
  }, [products]);

  return productsWithStock ?? [];
};

export default useStockAvailabilities;
